import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OneWayInServiceProviderDto } from 'src/api/v2';
import DataTableV3 from 'src/components/DataTableV3';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import useActorTypes from '../../hooks/use-actor-types';
import useCounties from '../../hooks/use-counties';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';
import { Counties, County } from 'src/features/one-way-in/constants/counties';
import useMunicipalities from '../../hooks/use-municipalities';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import { referralColumns } from '../../constants/referral-columns';

import {
  filterproviderCounty,
  filterproviderMunicipality,
  filterWithNormalization,
  filterServices,
  filterproviderPortfolioIndustries,
} from '../../utils/searchQueryHelper';
import useOneWayInServiceProviders from 'src/features/one-way-in/hooks/use-one-way-in-service-providers';
import { Button, Field, Icon, Input, Label, Suggestion, SuggestionItem } from '@innovationnorway/cl-react';
import ResetFilterButton from 'src/components/ResetFilterButton/ResetFilterButton';
import './ChooseActor.scss';

export type ActorFilters = {
  actorTypes: SuggestionItem[];
  counties: SuggestionItem[];
  municipalities: SuggestionItem[];
};

export const emptyActorFilters: ActorFilters = {
  actorTypes: [],
  counties: [],
  municipalities: [],
};

type Props = {
  formId: string | undefined;
  searchQuery: string;
  setSearchQuery: (value: React.SetStateAction<string>) => void;
  selectedActors?: OneWayInServiceProviderDto[];
  setSelectedRows: React.Dispatch<React.SetStateAction<OneWayInServiceProviderDto[]>>;
  filters: ActorFilters;
  setFilters: React.Dispatch<React.SetStateAction<ActorFilters>>;
};

const useCountyNameToMunicipalitiesNameMap = () => {
  const { getMunicipalityNameByNumber } = useGeonorge();
  const getMunicipalityNames = useCallback(
    (county: County) =>
      county.municipalities.map(
        (municipalityId) => getMunicipalityNameByNumber(municipalityId) ?? municipalityId,
      ),
    [getMunicipalityNameByNumber],
  );

  return useMemo<ReadonlyMap<string, ReadonlyArray<string>>>(
    () => new Map(Counties.map((county) => [county.countyName, getMunicipalityNames(county)])),
    [getMunicipalityNames],
  );
};

const useMunicipalitiesFromSelectedCounties = (
  selectedCountyIds: string[],
  unfilteredMunicipalities: SuggestionItem[],
) => {
  const countyToMunicipality = useCountyNameToMunicipalitiesNameMap();
  return useMemo(() => {
    if (selectedCountyIds.length === 0) return unfilteredMunicipalities;
    const municipalitiesFromCounties = new Set(
      //Denne split greia er cursed workaround for en constant fil som sammenlignes mot noe som ikke er konstant ¯\_(ツ)_/¯
      selectedCountyIds.map((countyId) => countyToMunicipality.get(countyId.split('–')[0].trimEnd())).flat(),
    );
    return unfilteredMunicipalities.filter((a) => municipalitiesFromCounties.has(a.value));
  }, [countyToMunicipality, selectedCountyIds, unfilteredMunicipalities]);
};

const ChooseActor: React.FC<Props> = ({
  formId,
  searchQuery,
  setSearchQuery,
  selectedActors,
  setSelectedRows,
  filters,
  setFilters,
}) => {
  const { cooperationServiceProviders, isLoading } = useOneWayInServiceProviders();
  const { getMunicipalityNameByNumber } = useGeonorge();
  const tableServiceProviders = useMemo(() => {
    return cooperationServiceProviders.map((provider) => ({
      ...provider,
      municipalities: provider.municipalities
        .map((municipality) => getMunicipalityNameByNumber(municipality) || municipality)
        .sort(),
    }));
  }, [cooperationServiceProviders, getMunicipalityNameByNumber]);

  const { oneWayInOpportunityById } = useOneWayIn(formId);
  const { t: tCommon } = useTranslation();
  const { t: tTableTitles } = useTranslation('tableTitles');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { actorTypes } = useActorTypes(tableServiceProviders);
  const { counties } = useCounties(tableServiceProviders);
  const { municipalities } = useMunicipalities(tableServiceProviders);

  const [selectedActorTypes, selectedCounties, selectedMunicipalities] = [
    filters.actorTypes,
    filters.counties,
    filters.municipalities,
  ];
  const [filtersOpen, setFiltersOpen] = useState(false);

  const selectedCountyIds = selectedCounties.map((c) => c.value);
  const selectedMunicipalityIds = selectedMunicipalities.map((m) => m.value);
  const selectedActorTypeIds = selectedActorTypes.map((a) => a.value);

  const municipalityOptions = useMunicipalitiesFromSelectedCounties(selectedCountyIds, municipalities);

  const filteredTableData = useMemo(() => {
    return (
      tableServiceProviders
        //Filter actor types
        .filter(
          (provider) =>
            selectedActorTypeIds.length === 0 ||
            selectedActorTypeIds.includes(provider.clusterStatusCode ?? '----'),
        )
        //Filter counties
        .filter(
          (provider) =>
            selectedCountyIds.length === 0 ||
            (provider.county && selectedCountyIds.includes(provider.county)),
        )
        // Filter municipalities
        .filter((provider) => {
          if (selectedMunicipalityIds.length === 0) return true;
          let included = false;
          provider.municipalities.forEach((muni) => {
            if (selectedMunicipalityIds.includes(muni)) {
              included = true;
            }
          });
          return included;
        })
    );
  }, [tableServiceProviders, selectedActorTypeIds, selectedCountyIds, selectedMunicipalityIds]);

  const searchedtableData = useMemo(() => {
    if (searchQuery === '') return filteredTableData;
    return filteredTableData.filter((actor) => {
      return (
        filterWithNormalization(actor.name ?? '', searchQuery) ||
        filterproviderMunicipality(actor, searchQuery) ||
        filterproviderCounty(actor, searchQuery) ||
        filterServices(actor, searchQuery) ||
        filterproviderPortfolioIndustries(actor, searchQuery)
      );
    });
  }, [searchQuery, filteredTableData]);

  function handleClearFilter() {
    setFilters(emptyActorFilters);
  }

  const preselectedRows = useMemo(
    () => Object.fromEntries((selectedActors ?? []).map((actor) => [actor.id, true])),
    [selectedActors],
  );

  return (
    <div className="choose-actor" data-filters-open={filtersOpen}>
      <Button
        type="button"
        variant="tertiary"
        className="choose-actor--filter-toggle margin-top--2"
        aria-expanded={filtersOpen}
        onClick={() => setFiltersOpen((open) => !open)}
      >
        <Icon prefix="fasr" iconName="filter" className="in-button--icon" />
        {tCommon('filters')}
      </Button>
      <section className="choose-actor--filters margin-top--2">
        <Field className="choose-actor--filter">
          <Label>{tOneWayIn('providerType')}</Label>
          <Suggestion
            multiple
            onSelectedChange={(selected) => setFilters((prev) => ({ ...prev, actorTypes: selected }))}
            selected={selectedActorTypes}
          >
            <Suggestion.Input placeholder={tOneWayIn('chooseProviderType')} />
            <Suggestion.Clear />
            <Suggestion.List>
              <Suggestion.Empty>{tOneWayIn('noFiltersAvailable')}</Suggestion.Empty>
              {actorTypes.map((option) => (
                <Suggestion.Option value={option.value} key={'actorType' + option.value}>
                  {option.label}
                </Suggestion.Option>
              ))}
            </Suggestion.List>
          </Suggestion>
        </Field>
        <Field className="choose-actor--filter">
          <Label>{tCommon('county')}</Label>
          <Suggestion
            multiple
            onSelectedChange={(selected) => setFilters((prev) => ({ ...prev, counties: selected }))}
            selected={selectedCounties}
          >
            <Suggestion.Input placeholder={tCommon('chooseCounty')} />
            <Suggestion.Clear />
            <Suggestion.List>
              <Suggestion.Empty>{tOneWayIn('noFiltersAvailable')}</Suggestion.Empty>
              {counties.map((option) => (
                <Suggestion.Option value={option.value} key={'county' + option.value}>
                  {option.label}
                </Suggestion.Option>
              ))}
            </Suggestion.List>
          </Suggestion>
        </Field>
        <Field className="choose-actor--filter">
          <Label>{tCommon('municipality')}</Label>
          <Suggestion
            multiple
            onSelectedChange={(selected) => setFilters((prev) => ({ ...prev, municipalities: selected }))}
            selected={selectedMunicipalities}
          >
            <Suggestion.Input placeholder={tOneWayIn('chooseMunicipality')} />
            <Suggestion.Clear />
            <Suggestion.List>
              <Suggestion.Empty>{tOneWayIn('noFiltersAvailable')}</Suggestion.Empty>
              {municipalityOptions.map((option) => (
                <Suggestion.Option value={option.value} key={'municipality' + option.value}>
                  {option.label}
                </Suggestion.Option>
              ))}
            </Suggestion.List>
          </Suggestion>
        </Field>
        {!oneWayInOpportunityById.isLoading && tableServiceProviders.length > 0 && (
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={tOneWayIn('filterByKeyword')}
            className="choose-actor--filter choose-actor--filter--wide"
          />
        )}
        <ResetFilterButton onClick={handleClearFilter} />
      </section>

      <div className="choose-actor--results margin-top--2">
        <DataTableV3
          id={DataTableV3Ids.OneWayIn.OpportunityDetailsReferring}
          disableGlobalFilter={true}
          hideFilterText
          data={searchedtableData}
          columns={referralColumns}
          translationFn={(key: string) => {
            return tTableTitles(key as any);
          }}
          texts={{
            globalFilterPlaceholder: tCommon('search'),
            rowsPerPage: tCommon('rowsPerPage'),
            of: tCommon('of'),
          }}
          pageSize={100}
          pageSizeOptions={[5, 10, 25, 100]}
          disableColumnEditor
          disableExport
          enableMultiRowSelection={false}
          getRowId={(row) => row.id}
          preselectedRows={preselectedRows}
          rowsSelectedSingularTranslation={tOneWayIn('details.Referral.Table.RowSelectedText')}
          rowsSelectedPluralTranslation={tOneWayIn('details.Referral.Table.RowsSelectedText')}
          setSelectedRows={setSelectedRows}
          loading={isLoading}
        />
      </div>
    </div>
  );
};

export default ChooseActor;
